// PWA utilities for SiteOrbit mobile app functionality

// Service Worker registration
export const registerSW = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
          
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // Show update available notification
                  showUpdateNotification();
                }
              });
            }
          });
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    });
  }
};

// Show update notification
const showUpdateNotification = () => {
  if (confirm('A new version of SiteOrbit is available. Would you like to update?')) {
    window.location.reload();
  }
};

// Install prompt handling
let deferredPrompt = null;

export const setupInstallPrompt = () => {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    
    // Show install button/banner
    showInstallBanner();
  });

  window.addEventListener('appinstalled', () => {
    console.log('PWA was installed');
    hideInstallBanner();
    deferredPrompt = null;
  });
};

export const triggerInstallPrompt = async () => {
  if (deferredPrompt) {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);
    deferredPrompt = null;
  }
};

// Install banner management
let installBanner = null;

const showInstallBanner = () => {
  if (installBanner) return;
  
  installBanner = document.createElement('div');
  installBanner.className = 'fixed bottom-4 left-4 right-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-2xl shadow-lg z-50 flex items-center justify-between';
  installBanner.innerHTML = `
    <div class="flex items-center">
      <svg class="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"></path>
      </svg>
      <div>
        <div class="font-semibold">Install SiteOrbit</div>
        <div class="text-sm opacity-90">Add to home screen for easy access</div>
      </div>
    </div>
    <div class="flex space-x-2">
      <button id="install-btn" class="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
        Install
      </button>
      <button id="dismiss-btn" class="text-white/70 hover:text-white p-2">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
        </svg>
      </button>
    </div>
  `;
  
  document.body.appendChild(installBanner);
  
  // Add event listeners
  document.getElementById('install-btn').addEventListener('click', triggerInstallPrompt);
  document.getElementById('dismiss-btn').addEventListener('click', hideInstallBanner);
  
  // Auto-hide after 10 seconds
  setTimeout(hideInstallBanner, 10000);
};

const hideInstallBanner = () => {
  if (installBanner) {
    installBanner.remove();
    installBanner = null;
  }
};

// Push notification setup
export const setupPushNotifications = async () => {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    console.log('Push notifications not supported');
    return false;
  }

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.log('Push notification permission denied');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY || '')
    });
    
    console.log('Push subscription:', subscription);
    return subscription;
  } catch (error) {
    console.error('Push subscription failed:', error);
    return false;
  }
};

// Helper function for VAPID key conversion
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Camera API for photo capture
export const capturePhoto = () => {
  return new Promise((resolve, reject) => {
    // Create camera input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use rear camera
    input.style.display = 'none';
    
    input.addEventListener('change', (event) => {
      const file = event.target.files[0];
      if (file) {
        resolve(file);
      } else {
        reject(new Error('No photo captured'));
      }
      document.body.removeChild(input);
    });
    
    input.addEventListener('cancel', () => {
      reject(new Error('Camera cancelled'));
      document.body.removeChild(input);
    });
    
    document.body.appendChild(input);
    input.click();
  });
};

// Enhanced camera with preview
export const capturePhotoWithPreview = () => {
  return new Promise((resolve, reject) => {
    // For more advanced camera functionality, we could integrate with getUserMedia
    // For now, using the file input approach which works reliably across devices
    capturePhoto().then(resolve).catch(reject);
  });
};

// Offline storage utilities
export const saveOfflineData = (key, data) => {
  try {
    localStorage.setItem(`siteorbit_offline_${key}`, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    return true;
  } catch (error) {
    console.error('Failed to save offline data:', error);
    return false;
  }
};

export const getOfflineData = (key) => {
  try {
    const stored = localStorage.getItem(`siteorbit_offline_${key}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.data;
    }
    return null;
  } catch (error) {
    console.error('Failed to get offline data:', error);
    return null;
  }
};

export const clearOfflineData = (key) => {
  try {
    localStorage.removeItem(`siteorbit_offline_${key}`);
    return true;
  } catch (error) {
    console.error('Failed to clear offline data:', error);
    return false;
  }
};

// Network status detection
export const isOnline = () => navigator.onLine;

export const setupNetworkListeners = (onOnline, onOffline) => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

// App shortcuts handling
export const handleAppShortcuts = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const action = urlParams.get('action');
  
  if (action) {
    console.log('PWA: Handling app shortcut action:', action);
    
    switch (action) {
      case 'upload':
        // Trigger photo upload after a delay to ensure page is loaded
        setTimeout(() => {
          const uploadBtn = document.querySelector('#fileInput');
          const cameraBtn = document.querySelector('button[onclick*="handleCameraCapture"]');
          
          if (uploadBtn) {
            uploadBtn.click();
            console.log('PWA: Triggered file upload');
          } else if (cameraBtn) {
            cameraBtn.click();
            console.log('PWA: Triggered camera capture');
          } else {
            console.log('PWA: No upload button found, trying alternative');
            // Try to find any upload-related button
            const uploadButtons = document.querySelectorAll('button');
            for (const btn of uploadButtons) {
              if (btn.textContent.toLowerCase().includes('camera') || 
                  btn.textContent.toLowerCase().includes('gallery') ||
                  btn.textContent.toLowerCase().includes('upload')) {
                btn.click();
                console.log('PWA: Triggered upload via', btn.textContent);
                break;
              }
            }
          }
        }, 2000);
        break;
        
      case 'signout':
        // Show sign out confirmation after delay
        setTimeout(() => {
          const signOutBtn = document.querySelector('button[data-action="signout"]');
          const accountBtn = document.querySelector('button:contains("Account Details")');
          
          if (signOutBtn) {
            signOutBtn.click();
            console.log('PWA: Triggered sign out');
          } else if (accountBtn) {
            accountBtn.click();
            console.log('PWA: Opened account details');
          } else {
            console.log('PWA: No sign out button found');
            // Try to find any sign out related button
            const buttons = document.querySelectorAll('button');
            for (const btn of buttons) {
              if (btn.textContent.toLowerCase().includes('sign out') || 
                  btn.textContent.toLowerCase().includes('logout') ||
                  btn.textContent.toLowerCase().includes('account')) {
                btn.click();
                console.log('PWA: Triggered action via', btn.textContent);
                break;
              }
            }
          }
        }, 2000);
        break;
        
      default:
        console.log('PWA: Unknown shortcut action:', action);
        break;
    }
    
    // Clean up URL parameters after handling (remove query params, keep clean path)
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
  }
};

// Background sync for photos
export const queuePhotoUpload = (file, siteName, token) => {
  // In a full implementation, this would store in IndexedDB
  // For now, we'll use localStorage as a simple fallback
  const uploads = JSON.parse(localStorage.getItem('siteorbit_pending_uploads') || '[]');
  
  const upload = {
    id: Date.now(),
    file: file, // Note: File objects can't be stringified, so this is simplified
    siteName,
    token,
    timestamp: Date.now()
  };
  
  uploads.push(upload);
  localStorage.setItem('siteorbit_pending_uploads', JSON.stringify(uploads));
  
  // Register background sync
  if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
    navigator.serviceWorker.ready.then((registration) => {
      return registration.sync.register('photo-upload');
    });
  }
};

// Haptic feedback for mobile devices
export const hapticFeedback = (type = 'light') => {
  if ('vibrate' in navigator) {
    switch (type) {
      case 'light':
        navigator.vibrate(50);
        break;
      case 'medium':
        navigator.vibrate(100);
        break;
      case 'heavy':
        navigator.vibrate([100, 50, 100]);
        break;
      case 'success':
        navigator.vibrate([100, 50, 100, 50, 100]);
        break;
      case 'error':
        navigator.vibrate([200, 100, 200]);
        break;
      default:
        navigator.vibrate(50);
    }
  }
};

// App badge API (for supported browsers)
export const setAppBadge = (count) => {
  if ('setAppBadge' in navigator) {
    navigator.setAppBadge(count);
  }
};

export const clearAppBadge = () => {
  if ('clearAppBadge' in navigator) {
    navigator.clearAppBadge();
  }
};

// Wake lock for photo uploads (prevent screen from sleeping)
let wakeLock = null;

export const requestWakeLock = async () => {
  try {
    if ('wakeLock' in navigator) {
      wakeLock = await navigator.wakeLock.request('screen');
      console.log('Wake lock acquired');
    }
  } catch (error) {
    console.error('Wake lock failed:', error);
  }
};

export const releaseWakeLock = () => {
  if (wakeLock) {
    wakeLock.release();
    wakeLock = null;
    console.log('Wake lock released');
  }
};

export default {
  registerSW,
  setupInstallPrompt,
  triggerInstallPrompt,
  setupPushNotifications,
  capturePhoto,
  capturePhotoWithPreview,
  saveOfflineData,
  getOfflineData,
  clearOfflineData,
  isOnline,
  setupNetworkListeners,
  handleAppShortcuts,
  queuePhotoUpload,
  hapticFeedback,
  setAppBadge,
  clearAppBadge,
  requestWakeLock,
  releaseWakeLock
};